import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { PlanFilters } from '../PlanFilters/PlanFilters';
import { PlanCard } from '../PlanCard/PlanCard';
import { useAgentPlans } from '../../hooks/useAgentPlans';
import { usePlanMutations } from '../../hooks/usePlanMutations';
import type { ActionPlan, PlanFiltersState, StepContent, RefusalReason } from '../../types/agent.types';

interface HistoryViewProps {
  filters: PlanFiltersState;
  onFiltersChange: (partial: Partial<PlanFiltersState>) => void;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Aujourd\'hui';
  if (date.toDateString() === yesterday.toDateString()) return 'Hier';

  return date.toLocaleDateString('fr-BE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function getResolvedDate(plan: ActionPlan): string {
  return plan.validated_at || plan.executed_at || plan.cancelled_at || plan.updated_at || plan.created_at;
}

function groupByDay(plans: ActionPlan[]) {
  // Sort all plans newest-first by validation/execution timestamp
  const sorted = [...plans].sort((a, b) =>
    new Date(getResolvedDate(b)).getTime() - new Date(getResolvedDate(a)).getTime()
  );

  const grouped = new Map<string, ActionPlan[]>();
  for (const plan of sorted) {
    const dateKey = new Date(getResolvedDate(plan)).toDateString();
    const existing = grouped.get(dateKey) || [];
    existing.push(plan);
    grouped.set(dateKey, existing);
  }
  return Array.from(grouped.entries());
}

export const HistoryView: React.FC<HistoryViewProps> = ({ filters, onFiltersChange }) => {
  const [localSearch, setLocalSearch] = useState(filters.search);
  const [highlightPlanId, setHighlightPlanId] = useState<string | null>(null);

  // Check if we were sent here from DecisionsView with a plan to highlight
  useEffect(() => {
    const planId = sessionStorage.getItem('agentLeaHighlightPlan');
    if (planId) {
      sessionStorage.removeItem('agentLeaHighlightPlan');
      setHighlightPlanId(planId);
      // Scroll to the plan after render
      setTimeout(() => {
        const el = document.getElementById(`plan-${planId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear highlight after 3s
          setTimeout(() => setHighlightPlanId(null), 3000);
        }
      }, 500);
    }
  }, []);

  const apiFilters = useMemo(
    () => ({ ...filters, search: '' }),
    [filters]
  );
  const { plans, isLoading } = useAgentPlans('history', apiFilters);
  const { reviewStep, validatePlan, retryPlan } = usePlanMutations();

  const filteredPlans = useMemo(() => {
    const q = localSearch.trim();
    if (!q) return plans;
    return plans.filter((p) => {
      const snapshot = p.affaire_snapshot || {};
      return (
        String(p.affaire_id).includes(q) ||
        (snapshot.client_name || '').toLowerCase().includes(q.toLowerCase()) ||
        (snapshot.address || '').toLowerCase().includes(q.toLowerCase())
      );
    });
  }, [plans, localSearch]);

  const groups = useMemo(() => groupByDay(filteredPlans), [filteredPlans]);

  const filtersWithSearch = useMemo(
    () => ({ ...filters, search: localSearch }),
    [filters, localSearch]
  );

  const handleFiltersChange = useCallback(
    (partial: Partial<PlanFiltersState>) => {
      if (partial.search !== undefined) {
        setLocalSearch(partial.search);
        return;
      }
      onFiltersChange(partial);
    },
    [onFiltersChange]
  );

  const handleReviewStep = useCallback(
    async (planId: string, stepNumber: number, review_status: string, modified_content?: StepContent, refusal_reason?: RefusalReason) => {
      await reviewStep({
        planId,
        stepNumber,
        review_status: review_status as any,
        modified_content,
        refusal_reason,
      });
    },
    [reviewStep]
  );

  const handleValidatePlan = useCallback(
    async (planId: string, comment?: string) => {
      await validatePlan({ planId, comment });
    },
    [validatePlan]
  );

  const handleRetryPlan = useCallback(
    async (planId: string) => {
      await retryPlan(planId);
    },
    [retryPlan]
  );

  return (
    <>
      <PlanFilters filters={filtersWithSearch} onFiltersChange={handleFiltersChange} />

      {isLoading ? (
        <div className="agent-loading">
          <span className="agent-spinner agent-spinner--default" />
        </div>
      ) : filteredPlans.length === 0 ? (
        <div className="agent-empty">
          <div className="agent-empty-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div className="agent-empty-title">Aucun historique</div>
          <div className="agent-empty-desc">Les plans executes et annules apparaitront ici</div>
        </div>
      ) : (
        <div>
          {groups.map(([dateKey, dayPlans]) => {
            const resolvedDate = getResolvedDate(dayPlans[0]);
            return (
              <div key={dateKey} className="agent-history-group">
                <div className="agent-history-date">
                  {formatDate(resolvedDate)} -- {dayPlans.length} plan{dayPlans.length > 1 ? 's' : ''}
                </div>
                {dayPlans.map((plan) => (
                  <div
                    key={plan.id}
                    id={`plan-${plan.id}`}
                    className={highlightPlanId === plan.id ? 'agent-plan-highlight' : ''}
                  >
                    <PlanCard
                      plan={plan}
                      onReviewStep={handleReviewStep}
                      onValidatePlan={handleValidatePlan}
                      onRetryPlan={handleRetryPlan}
                      isValidating={false}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
